import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { Badge } from "@/common/components/ui/badge";
import { Label } from "@/common/components/ui/label";
import { Button } from "@/common/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/common/components/ui/table";
import {
  Banknote,
  IndianRupee,
  Package,
  ArrowLeft,
  CreditCard,
  User,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { GoldService } from "@/services/APIs";

const PAYMENT_METHOD_OPTIONS = [
  "NEFT",
  "RTGS",
  "Bank Transfer",
  "Cheque",
  "UPI",
] as const;
const PAYMENT_STATUS_OPTIONS = ["Pending", "Paid"] as const;
const SETTLEMENT_STATUS_OPTIONS = ["Pending", "Settled"] as const;

const SettlementDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [settlement, setSettlement] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const fetchSettlementDetails = async () => {
      if (!id) {
        return;
      }
      try {
        setIsLoading(true);
        const res = await GoldService.settlementListById(id);
        const data = (res as any)?.data ?? res;

        if (data && (data.id || data.order_id)) {
          setSettlement(data);
        } else {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Settlement record not found",
          });
          navigate("/admin/physical-gold/settlements");
        }
      } catch (_err) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to fetch settlement details",
        });
        navigate("/admin/physical-gold/settlements");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettlementDetails();
  }, [id, navigate, toast]);

  if (isLoading || !settlement) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  const orderStatus = settlement.order_status?.toUpperCase() ?? "NEW";
  const isEditMode =
    new URLSearchParams(location.search).get("mode")?.toLowerCase() === "edit";
  const isViewMode = !isEditMode;

  const toCurrency = (value: string | number | null | undefined) =>
    `₹${parseFloat(String(value ?? "0")).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  const toPercent = (value: string | number | null | undefined) =>
    `${parseFloat(String(value ?? "0")).toFixed(2)}%`;

  const handleUpdatePaymentDetails = async () => {
    if (!id) {
      return;
    }

    setIsUpdating(true);
    try {
      const payload = {
        payment_method: settlement.payment_method ?? null,
        payment_status: settlement.payment_status
          ? String(settlement.payment_status).toUpperCase()
          : null,
        settlement_status: settlement.settlement_status
          ? String(settlement.settlement_status).toUpperCase()
          : null,
      };

      const response = await GoldService.updateSettlementById(id, payload);
      const updated = (response as any)?.data ?? response;
      if (updated && (updated.id || updated.order_id)) {
        setSettlement((prev: any) => ({ ...prev, ...updated }));
      }

      toast({
        title: "Updated",
        description: "Payment details updated successfully.",
      });
    } catch (_err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update payment details.",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50/50 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="mx-auto w-full max-w-7xl">
          <div className="flex flex-col gap-4 px-6 py-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-5">
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate("/admin/physical-gold/settlements")}
                className="h-10 w-10 border-slate-200 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50"
              >
                <ArrowLeft className="h-5 w-5 text-slate-600" />
              </Button>
              <div>
                <h1 className="font-display text-xl font-bold tracking-tight text-slate-900">
                  {isEditMode ? "Edit Settlement" : "Settlement Details"}
                </h1>
                <p className="text-sm font-medium text-slate-500">
                  Order Ref: {settlement.order_id ?? "-"} | MMTC Ref:{" "}
                  {settlement.mmtcid ?? "-"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-6 w-full max-w-7xl space-y-6 px-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* Main Settlement Financials */}
          <Card className="col-span-1 flex flex-col overflow-hidden rounded-2xl border border-[#66A0CD]/20 bg-gradient-to-br from-[#f3f8fc] via-[#edf4fa] to-[#e7f0f8] shadow-sm md:col-span-2">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-white p-2.5 shadow-sm ring-1 ring-[#66A0CD]/15">
                  <Banknote className="h-5 w-5 text-primary" />
                </div>
                <h2 className="font-display text-lg font-bold text-secondary">
                  Settlement Breakdown
                </h2>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-xl border border-white bg-white/70 p-4 backdrop-blur-sm">
                  <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Grand Total
                  </span>
                  <span className="mt-1 block text-lg font-black text-slate-800">
                    {toCurrency(settlement.grand_total)}
                  </span>
                </div>
                <div className="rounded-xl border border-white bg-white/70 p-4 backdrop-blur-sm">
                  <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    FinEase Comm.
                  </span>
                  <span className="mt-1 block text-lg font-black text-primary">
                    {toCurrency(settlement.commission_fin_ease)}
                  </span>
                </div>
                <div className="rounded-xl border border-white bg-white/70 p-4 backdrop-blur-sm">
                  <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Settlement Amount
                  </span>
                  <span className="mt-1 block text-lg font-black text-secondary">
                    {toCurrency(settlement.settlement_amount)}
                  </span>
                </div>
                <div className="rounded-xl border border-white bg-white/70 p-4 backdrop-blur-sm">
                  <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Gold Comm. %
                  </span>
                  <span className="mt-1 block text-lg font-black text-slate-800">
                    {toPercent(settlement.gold_commission_percentage)}
                  </span>
                </div>
                <div className="rounded-xl border border-white bg-white/70 p-4 backdrop-blur-sm">
                  <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Silver Comm. %
                  </span>
                  <span className="mt-1 block text-lg font-black text-slate-800">
                    {toPercent(settlement.silver_commission_percentage)}
                  </span>
                </div>
                <div className="rounded-xl border border-white bg-white/70 p-4 backdrop-blur-sm">
                  <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Gold Comm. Amount
                  </span>
                  <span className="mt-1 block text-lg font-black text-primary">
                    {toCurrency(settlement.gold_commission_amount)}
                  </span>
                </div>
                <div className="rounded-xl border border-white bg-white/70 p-4 backdrop-blur-sm">
                  <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Silver Comm. Amount
                  </span>
                  <span className="mt-1 block text-lg font-black text-primary">
                    {toCurrency(settlement.silver_commission_amount)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Info */}
          <Card className="flex flex-col rounded-2xl border-slate-100 bg-white shadow-sm">
            <CardHeader className="border-b border-slate-50 pb-4">
              <CardTitle className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
                <CreditCard className="h-4 w-4" /> Payment Status
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 space-y-4 pt-5">
              <div>
                <Label className="text-[10px] font-bold uppercase text-slate-400">
                  Payment Method
                </Label>
                <Select
                  value={settlement.payment_method ?? undefined}
                  disabled={isViewMode}
                  onValueChange={value =>
                    setSettlement((prev: any) => ({
                      ...prev,
                      payment_method: value,
                    }))
                  }
                >
                  <SelectTrigger className="mt-1 h-9 border-slate-200 bg-slate-50 text-left text-xs font-semibold text-slate-700">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHOD_OPTIONS.map(option => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-[10px] font-bold uppercase text-slate-400">
                  Payment Status
                </Label>
                <Select
                  value={
                    settlement.payment_status
                      ? settlement.payment_status.charAt(0).toUpperCase() +
                        settlement.payment_status.slice(1).toLowerCase()
                      : undefined
                  }
                  disabled={isViewMode}
                  onValueChange={value =>
                    setSettlement((prev: any) => ({
                      ...prev,
                      payment_status: value,
                    }))
                  }
                >
                  <SelectTrigger className="mt-1 h-9 border-slate-200 bg-slate-50 text-left text-xs font-semibold text-slate-700">
                    <SelectValue placeholder="Select payment status" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUS_OPTIONS.map(option => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-[10px] font-bold uppercase text-slate-400">
                  Settlement Status
                </Label>
                <Select
                  value={
                    settlement.settlement_status
                      ? settlement.settlement_status.charAt(0).toUpperCase() +
                        settlement.settlement_status.slice(1).toLowerCase()
                      : undefined
                  }
                  disabled={isViewMode}
                  onValueChange={value =>
                    setSettlement((prev: any) => ({
                      ...prev,
                      settlement_status: value,
                    }))
                  }
                >
                  <SelectTrigger className="mt-1 h-9 border-slate-200 bg-slate-50 text-left text-xs font-semibold text-slate-700">
                    <SelectValue placeholder="Select settlement status" />
                  </SelectTrigger>
                  <SelectContent>
                    {SETTLEMENT_STATUS_OPTIONS.map(option => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {settlement.transaction_id && (
                <div>
                  <Label className="text-[10px] font-bold uppercase text-slate-400">
                    Transaction ID
                  </Label>
                  <p className="mt-1 break-all font-mono text-xs font-bold text-slate-600">
                    {settlement.transaction_id}
                  </p>
                </div>
              )}
              {isEditMode && (
                <div className="pt-2">
                  <Button
                    type="button"
                    onClick={handleUpdatePaymentDetails}
                    disabled={isUpdating}
                    className="h-9 w-full bg-primary text-xs font-semibold text-white hover:bg-primary/90"
                  >
                    {isUpdating ? "Updating..." : "Update"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Key Dates & Order Status */}
          <Card className="rounded-2xl border-slate-100 bg-white shadow-sm">
            <CardHeader className="border-b border-slate-50 pb-4">
              <CardTitle className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
                <User className="h-4 w-4" /> Order & Logistics
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 pt-5">
              <div>
                <Label className="text-[10px] font-bold uppercase text-slate-400">
                  Order Date
                </Label>
                <p className="mt-1 text-sm font-semibold text-slate-700">
                  {settlement.order_date
                    ? new Date(settlement.order_date).toLocaleString()
                    : "-"}
                </p>
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase text-slate-400">
                  Order Status
                </Label>
                <div className="mt-1">
                  <Badge
                    variant="outline"
                    className="border-slate-200 bg-slate-50 text-[10px] text-slate-700"
                  >
                    {orderStatus.replace("_", " ")}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Price Mechanics */}
          <Card className="rounded-2xl border-slate-100 bg-white shadow-sm">
            <CardHeader className="border-b border-slate-50 pb-4">
              <CardTitle className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
                <IndianRupee className="h-4 w-4" /> Price Mechanics
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-5">
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between border-b border-slate-50/50 pb-2">
                  <span className="font-medium text-slate-500">Subtotal</span>
                  <span className="font-bold text-slate-700">
                    ₹{parseFloat(settlement.subtotal ?? "0").toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between border-b border-slate-50/50 pb-2">
                  <span className="font-medium text-slate-500">
                    GST ({settlement.gst_percentage ?? "3"}%)
                  </span>
                  <span className="font-bold text-slate-700">
                    ₹{parseFloat(settlement.gst_amount ?? "0").toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-2">
                  <span className="font-bold text-slate-900">
                    Total Customer Paid
                  </span>
                  <span className="text-lg font-black text-emerald-700">
                    ₹
                    {parseFloat(settlement.grand_total ?? "0").toLocaleString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Order Items — aligned with Order Details page */}
        <Card className="overflow-hidden rounded-xl border-slate-200 bg-white shadow-sm">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
              <Package className="h-4 w-4" /> Order Items (
              {settlement.items?.length ?? settlement.item_count ?? 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="h-12 text-[10px] font-bold uppercase text-slate-500">
                      Product
                    </TableHead>
                    <TableHead className="h-12 text-[10px] font-bold uppercase text-slate-500">
                      SKU
                    </TableHead>
                    <TableHead className="h-12 text-[10px] font-bold uppercase text-slate-500">
                      Purity
                    </TableHead>
                    <TableHead className="h-12 text-right text-[10px] font-bold uppercase text-slate-500">
                      Weight
                    </TableHead>
                    <TableHead className="h-12 text-right text-[10px] font-bold uppercase text-slate-500">
                      Qty
                    </TableHead>
                    <TableHead className="h-12 text-right text-[10px] font-bold uppercase text-slate-500">
                      Unit Price
                    </TableHead>
                    <TableHead className="h-12 text-right text-[10px] font-bold uppercase text-slate-500">
                      GST
                    </TableHead>
                    <TableHead className="h-12 text-right text-[10px] font-bold uppercase text-slate-500">
                      Commission
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(settlement.items ?? []).map((item: any, idx: number) => (
                    <TableRow
                      key={item.sku ?? item.id ?? idx}
                      className="border-slate-100 hover:bg-slate-50/50"
                    >
                      <TableCell className="py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-100">
                            {item.product_image ? (
                              <img
                                src={item.product_image}
                                alt={item.product_name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-slate-50 text-[10px] text-slate-400">
                                IMG
                              </div>
                            )}
                          </div>
                          <span className="text-sm font-medium text-slate-800">
                            {item.product_name ?? "-"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 font-mono text-xs font-medium text-slate-500">
                        {item.sku ?? "-"}
                      </TableCell>
                      <TableCell className="py-4">
                        <Badge
                          variant="outline"
                          className="border-amber-200 bg-amber-50 text-[10px] font-bold text-amber-700"
                        >
                          {item.purity ?? "-"}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-4 text-right font-bold text-amber-700">
                        {item.weight_grams ?? item.weight ?? "0"}g
                      </TableCell>
                      <TableCell className="py-4 text-right font-bold text-slate-700">
                        {item.quantity ?? 1}
                      </TableCell>
                      <TableCell className="py-4 text-right font-bold text-slate-700">
                        ₹{parseFloat(item.unit_price ?? "0").toLocaleString()}
                      </TableCell>
                      <TableCell className="py-4 text-right font-bold text-slate-700">
                        ₹{parseFloat(item.gst_amount ?? "0").toLocaleString()}
                      </TableCell>
                      <TableCell className="py-4 text-right font-bold text-amber-800">
                        ₹
                        {parseFloat(
                          item.commission_amount ?? "0"
                        ).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!settlement.items || settlement.items.length === 0) && (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="py-8 text-center text-sm font-medium text-slate-400"
                      >
                        No items found for this order.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Invoice-style summary footer */}
            <div className="border-t-2 border-slate-200 bg-slate-50/80">
              <div className="ml-auto flex w-full max-w-sm flex-col gap-0 px-6 py-4">
                <div className="flex items-center justify-between border-b border-dashed border-slate-200 py-2.5">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Subtotal
                  </span>
                  <span className="text-sm font-bold text-slate-700">
                    ₹
                    {parseFloat(settlement.subtotal ?? "0").toLocaleString(
                      "en-IN"
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between border-b border-dashed border-slate-200 py-2.5">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    GST ({settlement.gst_percentage ?? "3"}%)
                  </span>
                  <span className="text-sm font-bold text-slate-700">
                    ₹
                    {parseFloat(settlement.gst_amount ?? "0").toLocaleString(
                      "en-IN"
                    )}
                  </span>
                </div>
                {parseFloat(settlement.commission_fin_ease ?? "0") > 0 && (
                  <div className="flex items-center justify-between border-b border-dashed border-slate-200 py-2.5">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      Commission
                    </span>
                    <span className="text-sm font-bold text-slate-700">
                      ₹
                      {parseFloat(
                        settlement.commission_fin_ease ?? "0"
                      ).toLocaleString("en-IN")}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-3">
                  <span className="text-sm font-black uppercase tracking-wider text-slate-900">
                    Grand Total
                  </span>
                  <span className="text-lg font-black text-emerald-700">
                    ₹
                    {parseFloat(settlement.grand_total ?? "0").toLocaleString(
                      "en-IN"
                    )}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettlementDetailsPage;
